import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { getApiUrl } from '../utils/apiConfig';

const API = getApiUrl();

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gdiot-buddy-1/artifacts/2cgtc3o3_Gdiot%20new%20logo%20round%20%282%29.PNG";

export const StudentLogin = () => {
  const navigate = useNavigate();
  const { loginStudent } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedSchoolName, setSelectedSchoolName] = useState('');
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [helpNumber, setHelpNumber] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);

  useEffect(() => {
    fetchSchools();
    fetchHelpNumber();
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      fetchGrades(selectedSchool);
    } else {
      setGrades([]);
      setSelectedGrade('');
    }
  }, [selectedSchool]);

  const fetchSchools = async () => {
    try {
      const { data } = await axios.get(`${API}/schools`);
      setSchools(data);
    } catch (err) {
      setError('Failed to load schools');
    }
  };

  const fetchHelpNumber = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/help-number`);
      setHelpNumber(data.number || '');
    } catch (err) {
      // Error fetching help number
      // Non-critical - fail silently but log for debugging
    }
  };

  const fetchGrades = async (schoolId) => {
    try {
      const { data } = await axios.get(`${API}/grades/${schoolId}`);
      setGrades(data);
    } catch (err) {
      setError('Failed to load grades');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginStudent(firstName, lastName, selectedSchoolName, selectedGrade);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#f59e0b', '#10b981', '#3b82f6']
      });
      navigate('/student/dashboard');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (errorDetail === 'UNAUTHORIZED_STUDENT') {
        setShowUnauthorizedModal(true);
      } else {
        setError(errorDetail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 relative overflow-hidden px-4 py-8 md:py-0">
      {/* Background decorations - Responsive sizing */}
      <div className="absolute top-0 left-0 w-48 h-48 md:w-72 md:h-72 bg-amber-200 rounded-full opacity-30 -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-64 h-64 md:w-96 md:h-96 bg-amber-300 rounded-full opacity-20 translate-x-1/3 translate-y-1/3" />
      <div className="absolute top-1/4 right-4 md:right-10 w-16 h-16 md:w-24 md:h-24 bg-amber-400 rounded-full opacity-10" />
      <div className="absolute bottom-1/4 left-4 md:left-10 w-12 h-12 md:w-16 md:h-16 bg-amber-500 rounded-full opacity-10" />

      {/* Login Card - Full width on mobile, max-w-md on desktop */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border-2 border-amber-200">
          {/* Logo — Responsive sizing */}
          <div className="flex justify-center mb-4 md:mb-5">
            <img
              src={LOGO_URL}
              alt="GDIOT Buddy"
              className="h-20 w-20 sm:h-24 sm:w-24 md:h-32 md:w-32"
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>

          {/* Heading - Responsive text size */}
          <h1 className="text-2xl sm:text-3xl font-black font-heading text-amber-950 text-center tracking-tight">
            Welcome to <span className="text-amber-500">GDIOT Family!</span>
          </h1>
          <p className="text-amber-600 text-center mt-1 mb-5 md:mb-6 text-sm">
            Sign in to continue your learning journey
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name fields - Stack on mobile, side-by-side on larger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" data-testid="first-name-label" className="text-amber-800 font-bold text-sm mb-1.5 block">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  data-testid="first-name-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  placeholder="Shreya"
                  className="rounded-xl border-2 border-amber-200 focus:border-amber-500 bg-amber-50/50 h-12 sm:h-12 md:h-12 text-base font-medium placeholder:text-amber-300 touch-manipulation"
                />
              </div>
              <div>
                <Label htmlFor="lastName" data-testid="last-name-label" className="text-amber-800 font-bold text-sm mb-1.5 block">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  data-testid="last-name-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  placeholder="Sharma"
                  className="rounded-xl border-2 border-amber-200 focus:border-amber-500 bg-amber-50/50 h-12 sm:h-12 md:h-12 text-base font-medium placeholder:text-amber-300 touch-manipulation"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="school" data-testid="school-label" className="text-amber-800 font-bold text-sm mb-1.5 block">
                School
              </Label>
              <Select
                onValueChange={(value) => {
                  setSelectedSchool(value);
                  const school = schools.find(s => s.id === value);
                  setSelectedSchoolName(school?.name || '');
                }}
              >
                <SelectTrigger data-testid="school-select" className="rounded-xl border-2 border-amber-200 focus:border-amber-500 bg-amber-50/50 h-12 sm:h-12 md:h-12 text-base touch-manipulation">
                  <SelectValue placeholder="Select your school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.map((school) => (
                    <SelectItem key={school.id} value={school.id} className="touch-manipulation py-3">
                      {school.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="grade" data-testid="grade-label" className="text-amber-800 font-bold text-sm mb-1.5 block">
                Grade
              </Label>
              <Select
                onValueChange={setSelectedGrade}
                disabled={!selectedSchool}
              >
                <SelectTrigger data-testid="grade-select" className="rounded-xl border-2 border-amber-200 focus:border-amber-500 bg-amber-50/50 h-12 sm:h-12 md:h-12 text-base touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed">
                  <SelectValue placeholder="Select your grade" />
                </SelectTrigger>
                <SelectContent>
                  {grades.map((grade) => (
                    <SelectItem key={grade.id} value={grade.name} className="touch-manipulation py-3">
                      {grade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm border border-red-200" data-testid="error-message">
                {error}
              </div>
            )}

            {/* Primary CTA - Touch-optimized height (56px) */}
            <Button
              type="submit"
              data-testid="student-login-button"
              disabled={loading || !firstName || !lastName || !selectedSchool || !selectedGrade}
              className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 text-white rounded-2xl font-black h-14 sm:h-14 md:py-7 text-base sm:text-lg hover:-translate-y-1 transition-all shadow-lg shadow-amber-500/30 active:translate-y-0 active:shadow-md touch-manipulation"
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Logging in...
                </span>
              ) : (
                'START LEARNING'
              )}
            </Button>

            {/* Need Help Button - Touch-optimized height (52px) */}
            <a
              href={helpNumber ? `tel:${helpNumber}` : '#'}
              onClick={(e) => {
                if (!helpNumber) {
                  e.preventDefault();
                  setShowHelp(true);
                }
              }}
              data-testid="need-help-button"
              className="block w-full bg-green-500 hover:bg-green-600 active:bg-green-700 text-white rounded-2xl font-bold h-13 sm:h-13 md:py-4 text-base transition-all flex items-center justify-center gap-2 no-underline touch-manipulation"
              style={{ minHeight: '52px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Need Help?
            </a>

            {showHelp && !helpNumber && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center">
                <p className="text-amber-700 text-sm">Contact your school administrator for help.</p>
              </div>
            )}
          </form>
        </div>

        {/* Tagline - Responsive text size */}
        <p className="text-center text-amber-500 text-xs sm:text-sm mt-4 font-medium px-4">
          Every Genius was Once called an Idiot!
        </p>
      </div>

      {/* Unauthorized Student Modal */}
      <Dialog open={showUnauthorizedModal} onOpenChange={setShowUnauthorizedModal}>
        <DialogContent className="bg-white rounded-3xl border-2 border-red-200 max-w-md mx-4">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-bold text-red-600">
              Access Denied
            </DialogTitle>
            <DialogDescription className="text-center text-amber-900 text-lg mt-2">
              Invalid login. Make sure you're a part of the program.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-center">
            <p className="text-sm text-amber-700 mb-4">
              Please contact your school administrator to get added to the program.
            </p>
            <Button
              onClick={() => setShowUnauthorizedModal(false)}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold h-12"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
